import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Button } from './button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from './dialog.js';
/**
 * The portal's outcome messages.
 *
 * A modal rather than a toast, deliberately. A toast is an announcement: it is
 * gone in five seconds, it cannot be re-read, and it competes with whatever
 * the user looked at next. The outcome of an action they deliberately took —
 * a tenant created, a save refused — is a *result*, and a result should be
 * acknowledged before the screen moves on. That is what a modal enforces and a
 * toast cannot.
 *
 * Two things this is deliberately NOT for:
 *
 * - **Field validation.** "Email is required" belongs beside the email input,
 *   where the user is looking and where it stays while they fix it. Routing it
 *   here would take them away from the field and then hide the message.
 * - **Whole-page conditions.** A 403 on load is rendered by the screen itself;
 *   dismissing a modal to reveal an empty page explains nothing.
 */
export const FEEDBACK_TONE = {
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
    INFO: 'INFO',
};
const FeedbackContext = createContext(null);
const TONE_PRESENTATION = {
    SUCCESS: { icon: CircleCheck, className: 'text-success', defaultLabel: 'Done' },
    ERROR: { icon: CircleAlert, className: 'text-destructive', defaultLabel: 'Close' },
    INFO: { icon: Info, className: 'text-info', defaultLabel: 'Got it' },
};
export function FeedbackProvider({ children, }) {
    const [current, setCurrent] = useState(null);
    const showFeedback = useCallback((options) => {
        setCurrent(options);
    }, []);
    const showSuccess = useCallback((title, description) => {
        setCurrent({
            title,
            tone: FEEDBACK_TONE.SUCCESS,
            ...(description === undefined ? {} : { description }),
        });
    }, []);
    const showError = useCallback((title, description, reference) => {
        setCurrent({
            title,
            tone: FEEDBACK_TONE.ERROR,
            ...(description === undefined ? {} : { description }),
            ...(reference === undefined ? {} : { reference }),
        });
    }, []);
    const value = useMemo(() => ({ showFeedback, showSuccess, showError }), [showFeedback, showSuccess, showError]);
    const tone = current?.tone ?? FEEDBACK_TONE.INFO;
    const presentation = TONE_PRESENTATION[tone];
    const Icon = presentation.icon;
    const close = () => {
        current?.onAcknowledge?.();
        setCurrent(null);
    };
    return (_jsxs(FeedbackContext.Provider, { value: value, children: [children, _jsx(Dialog, { open: current !== null, onOpenChange: (open) => {
                    if (!open)
                        close();
                }, children: current === null ? null : (_jsxs(DialogContent, { hideClose: true, className: "max-w-md", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [_jsx(Icon, { className: `h-5 w-5 shrink-0 ${presentation.className}`, "aria-hidden": "true" }), current.title] }), current.description === undefined ? null : (_jsx(DialogDescription, { children: current.description }))] }), current.reference === undefined || current.reference === '' ? null : (_jsxs("p", { className: "text-caption text-muted-foreground", children: ["Reference ", _jsx("span", { className: "font-mono", children: current.reference })] })), _jsx(DialogFooter, { children: _jsx(Button, { onClick: close, children: current.confirmLabel ?? presentation.defaultLabel }) })] })) })] }));
}
export function useFeedback() {
    const context = useContext(FeedbackContext);
    if (context === null) {
        throw new Error('useFeedback must be used inside a <FeedbackProvider>');
    }
    return context;
}
//# sourceMappingURL=feedback-dialog.js.map